import { useQuery } from "@tanstack/react-query";
import { NotificationCenterKeys } from "web_myplann/Api/NotificationCenter/notificationCenterKeys";
import {
	useDocumentsFeatureOptional,
	useRemindersRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import {
	getNotificationCenterUpcomingV2,
	type TimelineHorizonEnum,
	type TimelineResponseDTO,
	type UpcomingTimelineItemTypeFilterEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { processResponseQuery } from "web_myplann/Infrastructure/Api/ApiUtils";
import { filterTimelineItemsByRights } from "web_myplann/NotificationCenter/Utils/filterTimelineItemsByRights";

import { resolveDocumentsOnlyFilter } from "web_myplann/NotificationCenter/Utils/resolveDocumentsOnlyFilter";

const STALE_TIME_MS = 60_000;

interface UpcomingTimelineParams {
	horizon: TimelineHorizonEnum;
	type: UpcomingTimelineItemTypeFilterEnum;
	documentsOnly?: boolean;
}

export const useUpcomingTimelineQuery = (
	params: UpcomingTimelineParams,
	options?: { enabled?: boolean },
) => {
	const hasRemindersRight = useRemindersRightOptional();
	const hasDocumentsFeature = useDocumentsFeatureOptional();
	const { documentsOnly, subcategories } = resolveDocumentsOnlyFilter(
		params.documentsOnly,
		hasDocumentsFeature,
	);

	return useQuery({
		queryKey: NotificationCenterKeys.upcoming(
			params.horizon,
			params.type,
			subcategories,
		),
		queryFn: processResponseQuery(() =>
			getNotificationCenterUpcomingV2(
				params.horizon,
				params.type,
				undefined,
				subcategories,
			),
		),
		enabled: options?.enabled ?? true,
		staleTime: STALE_TIME_MS,
		select: (data: TimelineResponseDTO) =>
			filterTimelineItemsByRights(data, {
				hasRemindersRight,
				hasDocumentsFeature,
				documentsOnly,
			}),
	});
};
